import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { comics, categories, faqs } from '@/mocks/comics';

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <span className="text-sm font-medium text-primary-500 bg-primary-100 px-3 py-1 rounded-full">常见问题</span>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-3 mb-2">有任何疑问？我们来解答</h2>
          <p className="text-sm text-foreground-500">关于布丁漫画的常见问题，快速找到你需要的答案</p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 text-sm transition-transform duration-200">
                  <i className={openIndex === index ? 'ri-subtract-line' : 'ri-add-line'} />
                </span>
              </button>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  openIndex === index ? 'max-h-96 pb-4 px-5' : 'max-h-0'
                }`}
              >
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const featuredComics = comics.filter(c => c.featured);
  const latestComics = [...comics].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 8);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '布丁漫画',
    url: 'https://puddingcomic.com',
    description: '布丁漫画 - 免费韩国漫画在线阅读平台，海量正版韩漫资源，恋爱、奇幻、动作、剧情等各类韩漫免费阅读。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://puddingcomic.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="免费韩国漫画弹窗入口 - 海量正版韩漫在线阅读平台"
        description="布丁漫画网设有免费韩国漫画弹窗入口，作为海量正版韩漫在线阅读平台，资源合法丰富，保障作者与读者权益，阅读安心。恋爱、奇幻、动作、剧情等各类韩漫免费阅读。"
        keywords="布丁漫画,免费韩国漫画,韩漫在线阅读,正版韩漫,韩国漫画平台,免费漫画弹窗入口"
        canonicalUrl="https://puddingcomic.com"
        schema={{ ...homeSchema, ...faqSchema }}
      />
      <Navbar />

      <main>
        <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20illustration%20with%20soft%20gradient%20colors%20in%20coral%20orange%20and%20warm%20rose%20pink%20tones%2C%20gentle%20flowing%20shapes%20like%20floating%20book%20pages%20and%20stylized%20comic%20panels%2C%20dreamy%20atmospheric%20mood%2C%20minimal%20clean%20composition%2C%20soft%20diffused%20lighting%2C%20elegant%20modern%20design%2C%20no%20text%2C%20high%20quality%20digital%20art&width=1800&height=900&seq=hero-bg-01&orientation=landscape"
            alt="布丁漫画 - 免费韩国漫画阅读平台"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <span className="inline-block text-sm font-medium text-background-50/90 bg-background-50/15 backdrop-blur-sm px-4 py-1.5 rounded-full mb-5">
              海量正版韩漫 · 永久免费阅读
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
              布丁漫画
            </h1>
            <p className="text-base md:text-lg text-background-50/80 max-w-xl mx-auto mb-8 leading-relaxed">
              免费韩国漫画弹窗入口，海量正版韩漫在线阅读平台。资源合法丰富，保障作者与读者权益，阅读安心。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/romance"
                className="inline-flex items-center px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-all cursor-pointer"
              >
                <i className="ri-book-open-line mr-2" />
                立即阅读
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center px-8 py-3 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 text-sm font-medium whitespace-nowrap border border-background-50/30 hover:bg-background-50/25 transition-all cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 bg-background-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { num: '10,000+', label: '正版漫画' },
              { num: '50+', label: '合作出版社' },
              { num: '200万+', label: '日活跃用户' },
              { num: '50亿+', label: '累计阅读量' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.num}</div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
          <div className="text-center mb-10">
            <span className="text-sm font-medium text-primary-500 bg-primary-100 px-3 py-1 rounded-full">热门推荐</span>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-3 mb-2">大家都在看</h2>
            <p className="text-sm text-foreground-500">精选最受欢迎的韩国漫画，品质保证</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {featuredComics.map((comic) => (
              <Link
                key={comic.slug}
                to={`/article/${comic.slug}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded-full">
                    {comic.rating}
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{comic.title}</h3>
                <p className="text-xs text-foreground-500 mt-1">{comic.author} · {comic.status}</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
          <div className="text-center mb-10">
            <span className="text-sm font-medium text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full">漫画分类</span>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-3 mb-2">找到你喜欢的类型</h2>
            <p className="text-sm text-foreground-500">多种分类任你选择，总有一款适合你</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex items-center gap-3 px-4 py-4 rounded-lg bg-background-100 border border-background-200 hover:border-secondary-300 hover:bg-secondary-50 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 group-hover:bg-secondary-500 group-hover:text-background-50 transition-colors flex-shrink-0">
                  <i className={`${cat.icon} text-lg`} />
                </div>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="text-sm font-medium text-accent-500 bg-accent-100 px-3 py-1 rounded-full">最新更新</span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-3">追更不等待</h2>
            </div>
            <Link to="/category/romance" className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap font-medium transition-colors">
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {latestComics.map((comic) => (
              <Link
                key={comic.slug}
                to={`/article/${comic.slug}`}
                className="group flex gap-3 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={comic.thumbImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">{comic.title}</h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                  <div className="flex items-center gap-1 mt-1.5">
                    <span className="text-xs text-accent-500 bg-accent-100 px-1.5 py-0.5 rounded-full">第{comic.chapters}话</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8 sm:hidden">
            <Link to="/category/romance" className="inline-flex items-center gap-1 text-sm text-primary-500 font-medium">
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-sm font-medium text-primary-500 bg-primary-100 px-3 py-1 rounded-full">为什么选择我们</span>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-3 mb-2">布丁漫画的核心优势</h2>
            <p className="text-sm text-foreground-500 mb-10">我们致力于为中文读者提供最优质的韩漫阅读体验</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '正版授权', desc: '所有漫画均获得韩国出版社合法授权，保障创作者权益，让读者阅读安心。' },
                { icon: 'ri-currency-line', title: '永久免费', desc: '平台通过广告和补贴模式运营，所有漫画对读者永久免费开放，无需付费即可畅读。' },
                { icon: 'ri-translate', title: '专业翻译', desc: '资深韩语翻译团队精心本地化，保留原作魅力的同时确保中文表达自然流畅。' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '网站全面适配PC、平板和手机端，随时随地享受流畅的漫画阅读体验。' },
                { icon: 'ri-rocket-line', title: '极速更新', desc: '与韩国同步更新，热门漫画第一时间翻译上线，让你追更快人一步。' },
                { icon: 'ri-user-heart-line', title: '社区互动', desc: '活跃的读者社区，评论、评分、收藏功能齐全，与其他漫迷一起分享阅读乐趣。' },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center text-center p-6 rounded-lg bg-background-100 border border-background-200">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                    <i className={`${item.icon} text-xl`} />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FAQSection />
      </main>

      <Footer />
    </>
  );
}